/**
 * Values of a restaurant's `pos_provider`.
 *
 * `none` means no POS has been chosen yet — a restaurant our data collector
 * scraped and its owner has since claimed. It edits its menu, hours and
 * profile exactly like a Fork & Find restaurant, but takes no orders until the
 * owner enables Fork & Find (`fnf`) or connects Square / Clover / Swirepay.
 */
export const POS_PROVIDER = {
  FNF: 'fnf',
  SQUARE: 'square',
  CLOVER: 'clover',
  // Swirepay merchants run Clover hardware, but their catalogue and orders go
  // through Swirepay's inventory API, so it is a provider of its own.
  SWIREPAY: 'swirepay',
  NONE: 'none',
} as const;

/** Providers whose catalogue is synced from the POS, so menu/type/customization
 * editing is read-only in the dashboard. */
const POS_MANAGED_PROVIDERS: string[] = [
  POS_PROVIDER.SQUARE,
  POS_PROVIDER.CLOVER,
  POS_PROVIDER.SWIREPAY,
];

/** True when the menu catalogue is owned by an external POS (Square / Clover /
 * Swirepay). Use this rather than comparing provider strings so a new POS only
 * has to be added to the list above. */
export function isPosManaged(provider: string | null | undefined): boolean {
  return POS_MANAGED_PROVIDERS.includes((provider || '').toLowerCase());
}

/**
 * True when the dashboard owns the catalogue — Fork & Find, or no POS chosen
 * yet (`none`). Use this rather than `=== 'fnf'` for editing gates, so a
 * restaurant without a POS can still create, edit and delete its menu.
 */
export function isFnfLike(provider: string | null | undefined): boolean {
  const value = (provider || '').toLowerCase();
  return value === POS_PROVIDER.FNF || value === POS_PROVIDER.NONE;
}

/** True when the owner has not chosen how they'll take orders yet. */
export function isPosNotSelected(provider: string | null | undefined): boolean {
  return (provider || '').toLowerCase() === POS_PROVIDER.NONE;
}

/** Display names per provider, shared by the Hardware page and anywhere else
 * that names the restaurant's POS. */
export const POS_PROVIDER_NAMES: Record<string, string> = {
  [POS_PROVIDER.FNF]: 'Fork & Find',
  [POS_PROVIDER.CLOVER]: 'Clover',
  [POS_PROVIDER.SQUARE]: 'Square',
  [POS_PROVIDER.SWIREPAY]: 'Swirepay',
};

/** sessionStorage key holding the restaurant id whose choose-POS prompt was
 * dismissed with "Later". Cleared on logout so the prompt returns next login. */
export const CHOOSE_POS_DISMISSED_KEY = 'choosePosDismissed';
